/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { StickyNote, X, Send, Sparkles, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { addClinicalNote, listClinicalNotes } from '@/lib/telemedicine';
import { exportSoapAsFhir } from '@/lib/sprint5';
import { SoapSummaryPanel } from '@/components/telemedicine/SoapSummaryPanel';
import { Icd10Suggester } from '@/components/telemedicine/Icd10Suggester';
import { VoiceToSoapButton } from '@/components/telemedicine/VoiceToSoapButton';

/**
 * Slide-in right panel used inside the VideoRoom for doctor clinical notes,
 * prescriptions, diagnosis and AI (SOAP + ICD-10) helpers.
 *
 * Extracted from VideoRoomPage.jsx (Phase 8 refactor).
 */
export const NotesPanel = ({ apptId, onClose }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [prescriptions, setPrescriptions] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  // Track the latest SOAP state so we can export a well-structured FHIR
  // Encounter rather than parsing the free-text notes textarea.
  const [currentSoap, setCurrentSoap] = useState(null);
  const [exportingFhir, setExportingFhir] = useState(false);
  const [sessionStartedAt] = useState(() => new Date().toISOString());

  const load = React.useCallback(() => {
    listClinicalNotes(apptId)
      .then((r) => setHistory(r.notes || []))
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
           
          console.debug('[NotesPanel] listClinicalNotes failed', err);
        }
      });
  }, [apptId]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!notes.trim()) return;
    setSaving(true);
    try {
      await addClinicalNote(apptId, {
        notes,
        prescriptions: prescriptions || undefined,
        diagnosis: diagnosis || undefined,
      });
      toast.success(t('noteSaved'));
      setNotes('');
      setPrescriptions('');
      setDiagnosis('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
      className="absolute top-0 right-0 h-full w-full sm:w-[420px] glass-strong border-l border-border/60 flex flex-col z-30"
      data-testid="notes-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-primary" />
          <div className="font-display font-semibold">{t('notesTitle')}</div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="notes-close-btn">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-4">
        {/* Voice-to-SOAP dictation (hands-free note authoring) */}
        <VoiceToSoapButton
          onSoapUpdate={({ soap, formattedNotes }) => {
            // Replace the notes textarea with the auto-formatted SOAP block —
            // the doctor can still edit it manually before saving.
            setNotes(formattedNotes);
            // Auto-fill Diagnosis (A) and Prescriptions (P) when spoken.
            if (soap.assessment) setDiagnosis(soap.assessment);
            if (soap.plan) setPrescriptions(soap.plan);
            setCurrentSoap(soap);
          }}
        />
        <div>
          <Label htmlFor="notes-input">{t('notesClinical')}</Label>
          <Textarea
            id="notes-input"
            rows={5}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('notesClinicalPh')}
            data-testid="notes-input"
          />
        </div>
        <div className="grid gap-3">
          <div>
            <Label htmlFor="notes-diagnosis">{t('notesDiagnosis')}</Label>
            <Input
              id="notes-diagnosis"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder={t('notesDiagnosisPh')}
              data-testid="notes-diagnosis-input"
            />
          </div>
          <div>
            <Label htmlFor="notes-prescriptions">{t('notesPrescriptions')}</Label>
            <Textarea
              id="notes-prescriptions"
              rows={3}
              value={prescriptions}
              onChange={(e) => setPrescriptions(e.target.value)}
              placeholder={t('notesPrescriptionsPh')}
              data-testid="notes-prescriptions-input"
            />
          </div>
        </div>
        <Button
          onClick={save}
          disabled={!notes.trim() || saving}
          className="w-full"
          data-testid="notes-save-btn"
        >
          {saving ? (
            t('processing')
          ) : (
            <>
              <Send className="h-3.5 w-3.5" />
              {t('notesSave')}
            </>
          )}
        </Button>

        <Button
          onClick={async () => {
            // Prefer the tracked SOAP state; fall back to a naive parse of
            // the current textareas so the button still works even if the
            // doctor typed everything manually.
            const soap = currentSoap && (currentSoap.subjective || currentSoap.objective ||
                                          currentSoap.assessment || currentSoap.plan)
              ? currentSoap
              : {
                  subjective: notes || '',
                  objective: '',
                  assessment: diagnosis || '',
                  plan: prescriptions || '',
                };
            const hasContent = soap.subjective || soap.objective || soap.assessment || soap.plan;
            if (!hasContent) {
              toast.error('Compila almeno una sezione SOAP prima di esportare');
              return;
            }
            setExportingFhir(true);
            try {
              const res = await exportSoapAsFhir({
                appointment_id: apptId,
                subjective: soap.subjective || undefined,
                objective: soap.objective || undefined,
                assessment: (soap.assessment || diagnosis) || undefined,
                plan: (soap.plan || prescriptions) || undefined,
                language: (soap.lastLang || 'it'),
                started_at: sessionStartedAt,
                ended_at: new Date().toISOString(),
              });
              // Trigger a browser download of the Bundle JSON.
              const blob = new Blob(
                [JSON.stringify(res.bundle, null, 2)],
                { type: 'application/fhir+json' },
              );
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `soap-encounter-${apptId || 'session'}-${Date.now()}.json`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              toast.success(`Bundle FHIR generato · ${res.resource_count} risorse`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Export FHIR fallito');
            } finally {
              setExportingFhir(false);
            }
          }}
          disabled={exportingFhir}
          variant="outline"
          className="w-full"
          data-testid="notes-fhir-export-btn"
        >
          <FileDown className="h-3.5 w-3.5 mr-1" />
          {exportingFhir ? 'Generazione…' : 'Esporta FHIR (HL7 R4)'}
        </Button>

        {/* AI: SOAP Summary + ICD-10 suggestions */}
        <div className="pt-3 border-t border-border/60 space-y-3">
          <div className="text-[10px] uppercase tracking-wider text-primary font-bold flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> AI Clinical Assistant
          </div>
          <SoapSummaryPanel apptId={apptId} sourceText={notes} />
          <Icd10Suggester noteText={notes} apptId={apptId} />
        </div>

        {history.length > 0 && (
          <div className="pt-4 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
              {t('notesHistory')}
            </div>
            <div className="space-y-3">
              {history.map((n) => (
                <div
                  key={n.id}
                  className="rounded-xl border border-border/60 p-3 bg-card/50"
                  data-testid={`note-history-${n.id}`}
                >
                  <div className="text-[10px] text-muted-foreground">
                    {new Date(n.created_at).toLocaleString('it')}
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{n.notes}</p>
                  {n.diagnosis && (
                    <div className="text-xs mt-1">
                      <span className="font-semibold">Dx:</span> {n.diagnosis}
                    </div>
                  )}
                  {n.prescriptions && (
                    <div className="text-xs mt-1 text-primary">
                      <span className="font-semibold">Rx:</span> {n.prescriptions}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default NotesPanel;
